import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-black" data-testid="terms-conditions-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
                alt="AIP Logo" 
                className="h-20 w-auto"
              />
            </Link>
            <Link to="/" className="text-neutral-400 hover:text-white flex items-center gap-2">
              <ArrowLeft size={18} /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold mb-2">Terms and Conditions</h1>
          <p className="text-neutral-500 mb-8">Last updated: December 2026</p>

          <div className="prose prose-invert max-w-none space-y-8 text-neutral-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
              <p>
                These Terms and Conditions ("Terms") govern your use of the AIP Limited website and our property inventory services. By accessing our website or using our services, you agree to be bound by these Terms.
              </p>
              <p>
                AIP Limited is a company registered in England and Wales. These Terms are governed by English law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Definitions</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>"Company", "we", "us", "our"</strong> refers to AIP Limited</li>
                <li><strong>"Client", "you", "your"</strong> refers to the person or entity using our services</li>
                <li><strong>"Services"</strong> refers to property inventory reports, check-in/check-out inspections, mid-term inspections, snagging surveys, and related services</li>
                <li><strong>"Property"</strong> refers to the residential or commercial premises subject to our services</li>
                <li><strong>"Report"</strong> refers to any written documentation produced as a result of our services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Our Services</h2>
              <h3 className="text-xl font-medium text-white mb-2">3.1 Service Description</h3>
              <p>We provide the following property inventory services:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li><strong>Inventory Reports:</strong> Detailed documentation of property condition, fixtures, fittings, and contents</li>
                <li><strong>Check-In Inspections:</strong> Property condition assessment at the start of a tenancy</li>
                <li><strong>Check-Out Inspections:</strong> Property condition assessment at the end of a tenancy, comparing against the original inventory</li>
                <li><strong>Mid-Term Inspections:</strong> Periodic property inspections during a tenancy</li>
                <li><strong>Snagging Surveys:</strong> Identification of defects in new build properties</li>
              </ul>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">3.2 Service Standards</h3>
              <p>
                All services are carried out in accordance with industry best practices. Our clerks are trained professionals who adhere to the standards set by the Association of Independent Inventory Clerks (AIIC).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Bookings and Appointments</h2>
              <h3 className="text-xl font-medium text-white mb-2">4.1 Making a Booking</h3>
              <p>
                Bookings can be made through our website, by telephone, or by email. All bookings are subject to availability and confirmation.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">4.2 Access Requirements</h3>
              <p>
                You must ensure that our clerk has access to the property at the agreed time. If access cannot be provided, you must notify us at least 24 hours in advance to reschedule.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">4.3 Cancellation Policy</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Cancellations made more than 48 hours before the appointment: No charge</li>
                <li>Cancellations made 24-48 hours before the appointment: 50% of the quoted price</li>
                <li>Cancellations made less than 24 hours before the appointment or no access provided: Full charge applies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Pricing and Payment</h2>
              <h3 className="text-xl font-medium text-white mb-2">5.1 Pricing</h3>
              <p>
                Our prices are based on the property size and services required. Quotes provided are valid for 30 days. Additional charges may apply for properties that are significantly larger, cluttered, or in poor condition requiring extra documentation time.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">5.2 Payment Terms</h3>
              <p>
                Payment is due within 14 days of invoice date unless otherwise agreed. We accept payment by bank transfer and major credit/debit cards. Reports will be released upon receipt of payment.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">5.3 Late Payment</h3>
              <p>
                We reserve the right to charge interest on overdue amounts at 4% above the Bank of England base rate, in accordance with the Late Payment of Commercial Debts (Interest) Act 1998.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Reports and Documentation</h2>
              <h3 className="text-xl font-medium text-white mb-2">6.1 Report Delivery</h3>
              <p>
                Reports are typically delivered within 24-48 hours of the inspection, subject to complexity. Reports are provided in PDF format via the client portal or email.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">6.2 Report Accuracy</h3>
              <p>
                Our reports provide an accurate record of the property's condition at the time of inspection. We document visible defects, damage, and cleanliness. We do not test electrical appliances, plumbing systems, or other installations unless specifically agreed.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">6.3 Amendments</h3>
              <p>
                Requests for amendments to reports must be made within 7 days of receipt. Legitimate errors or omissions will be corrected free of charge. Additional inspections or substantial amendments may incur additional charges.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">6.4 Intellectual Property</h3>
              <p>
                All reports, photographs, and documentation remain our intellectual property. Clients are granted a licence to use reports for their intended purpose (tenancy documentation, deposit disputes, etc.). Reports may not be altered, resold, or used for commercial purposes without our written consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Liability</h2>
              <h3 className="text-xl font-medium text-white mb-2">7.1 Limitation of Liability</h3>
              <p>
                Our liability for any claim arising from our services is limited to the fee paid for that specific service. We shall not be liable for any indirect, consequential, or special damages.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">7.2 Exclusions</h3>
              <p>We are not liable for:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Defects or issues not visible at the time of inspection</li>
                <li>Items concealed, inaccessible, or behind locked doors/cupboards</li>
                <li>The functionality of appliances, heating, plumbing, or electrical systems</li>
                <li>Pre-existing damage not documented in previous reports</li>
                <li>Disputes between landlords and tenants</li>
              </ul>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">7.3 Insurance</h3>
              <p>
                We maintain professional indemnity insurance. Details are available upon request.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Client Portal</h2>
              <h3 className="text-xl font-medium text-white mb-2">8.1 Account Security</h3>
              <p>
                You are responsible for maintaining the confidentiality of your account login credentials. You must notify us immediately of any unauthorised access to your account.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">8.2 Acceptable Use</h3>
              <p>
                You agree to use the client portal only for its intended purpose. You must not attempt to access other clients' data, interfere with the portal's operation, or use automated systems to access the portal.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Website Use</h2>
              <h3 className="text-xl font-medium text-white mb-2">9.1 Website Content</h3>
              <p>
                The content on our website is for general information purposes. While we strive to keep information accurate and up-to-date, we make no warranties about the completeness or accuracy of the information provided.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">9.2 External Links</h3>
              <p>
                Our website may contain links to external websites. We are not responsible for the content or privacy practices of these external sites.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Complaints</h2>
              <p>
                If you are dissatisfied with our services, please contact us in writing within 14 days. We will acknowledge your complaint within 5 working days and aim to resolve it within 28 days. Our complaints procedure is available upon request.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. General Provisions</h2>
              <h3 className="text-xl font-medium text-white mb-2">11.1 Entire Agreement</h3>
              <p>
                These Terms constitute the entire agreement between you and AIP Limited regarding the use of our website and services.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">11.2 Severability</h3>
              <p>
                If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">11.3 Waiver</h3>
              <p>
                Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
              </p>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">11.4 Governing Law</h3>
              <p>
                These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Changes will be effective when posted on this page. Continued use of our website or services after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Information</h2>
              <p>
                For questions about these Terms and Conditions, please contact:
              </p>
              <p className="mt-2">
                AIP Limited<br />
                Email: <a href="mailto:inventories@aiplimited.co.uk" className="text-[#B8960B] hover:underline">inventories@aiplimited.co.uk</a><br />
                Phone: +44 7841 238 061<br />
                Location: Manchester, United Kingdom
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsConditions;
